// React Imports
import { useState, useEffect, useMemo } from "react";

// External Libraries
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import T from "prop-types";

// Hooks & Helpers
import {
  buildUserProfilePayload,
  formatDeveloperProfile,
  normalizeToolingWithOther,
} from "../../../helpers/userProfile";
import { fetchCountriesList, uploadUserImage, updateUser } from "sj-ui-toolkit/src/api";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";

// Local Components
import { ProfileField } from "./ProfileField";
import S from "./ProfileDetailsTab.module.scss";

// Constants
import {
  FIELD_GROUPS,
  PROFILE_FIELD_KEYS,
  SSO_LOCKED_FIELDS,
  ALWAYS_LOCKED_FIELDS,
} from "../../../constants/profileDetailsFields";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";

export function ProfileDetailsTab() {
  useBreadcrumb({ label: "Profile", route: "/profile", display: "page" });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const { user, setUser } = useUserContext();
  const { settings } = useSettingsContext();

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countriesList = await fetchCountriesList();
        setCountries(countriesList);
      } catch (e) {
        console.error("Error fetching countries:", e);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    if (!user) return;

    const formatted = formatDeveloperProfile(user);
    const { tooling, toolingOther } = normalizeToolingWithOther(formatted.tooling);

    setFormData({
      ...formatted,
      languageOther: "",
      toolingOther,
      tooling,
      sso: settings?.sso,
      allowProfileEditing: settings?.allowUserProfileEdits,
    });
  }, [user, settings]);

  // Clear language and tooling fields when user selects "does not work with code"
  useEffect(() => {
    if (!formData) return;

    const expertise = formData.expertise;

    if (expertise === DeveloperProfileConstants.VALUES.EXPERTISE_DOES_NOT_WORK_WITH_CODE) {
      setFormData(prev => ({
        ...prev,
        language: "",
        languageOther: "",
        tooling: [],
        toolingOther: "",
      }));
    }
  }, [formData?.expertise]);

  const disableSave = useMemo(() => {
    if (!formData) return false;

    const { language, tooling, languageOther, toolingOther } = formData;
    const languageOtherSelectedAndMissing = language === "other" && !languageOther?.trim();

    const toolingOtherSelectedAndMissing =
      Array.isArray(tooling) && tooling.includes("other") && !toolingOther?.trim();

    return languageOtherSelectedAndMissing || toolingOtherSelectedAndMissing;
  }, [formData]);

  const formDisabled = useMemo(() => {
    if (!settings) return false;

    const { sso, allowUserProfileEdits } = settings;

    if (sso) {
      // SSO on: allowProfileEdits takes precedence
      return !allowUserProfileEdits;
    }

    // SSO off: if allowProfileEdits is false, disable
    if (!allowUserProfileEdits) {
      return true;
    }

    return false;
  }, [settings]);

  const shouldDisableField = fieldKey => {
    const expertise = formData?.expertise;

    if (expertise === DeveloperProfileConstants.VALUES.EXPERTISE_DOES_NOT_WORK_WITH_CODE) {
      return [PROFILE_FIELD_KEYS.LANGUAGE, PROFILE_FIELD_KEYS.TOOLING].includes(fieldKey);
    }

    return false;
  };

  const isFieldLocked = (fieldKey, formDisabled) => {
    if (ALWAYS_LOCKED_FIELDS.includes(fieldKey)) return true;
    if (formDisabled && SSO_LOCKED_FIELDS.includes(fieldKey)) return true;
    return false;
  };

  const applyFieldOptions = (field, countries) => {
    if (field.key === PROFILE_FIELD_KEYS.COUNTRY) return { ...field, options: countries };
    return field;
  };

  const dynamicFieldGroups = useMemo(() => {
    return FIELD_GROUPS.map(group => ({
      ...group,
      fields: group.fields.map(field =>
        applyFieldOptions(
          {
            ...field,
            locked: isFieldLocked(field.key, formDisabled),
            disabled: shouldDisableField(field.key),
          },
          countries
        )
      ),
    }));
  }, [countries, formDisabled, formData?.expertise]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleProfilePhotoUpload = async ({ files, options }) => {
    try {
      const file = files?.[0];
      if (!file) return;
      setIsUploadingPhoto(true);

      const response = await uploadUserImage(file);
      const { small_image_url: smallImageUrl } = response;

      // Update UserContext with new profile image URL so it displays immediately
      // without requiring a page reload or refetch
      setUser(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          railsProfile: {
            ...prev.railsProfile,
            smallImageUrl,
          },
        };
      });

      if (options?.clear) options.clear();
    } catch (error) {
      console.error("Error uploading profile photo:", error);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleEditProfileClick = async () => {
    if (!isEditing) return setIsEditing(true);

    const payload = buildUserProfilePayload(formData);

    try {
      await updateUser(payload);
      // if successful, update UserContext
      setUser(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          ...payload,
        };
      });
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsEditing(false);
    }
  };

  if (!formData) return null;

  return (
    <>
      <div className="my-5 flex gap-3">
        <Button
          label={isEditing ? "Save" : "Edit Profile"}
          onClick={handleEditProfileClick}
          icon={!isEditing && "pi pi-pencil"}
          disabled={disableSave}
        />
        <FileUpload
          mode="basic"
          name="profilePhoto"
          accept="image/png, image/jpeg"
          maxFileSize={5000000}
          chooseLabel={isUploadingPhoto ? "Uploading..." : "Upload profile photo"}
          disabled={isUploadingPhoto}
          customUpload
          auto
          uploadHandler={handleProfilePhotoUpload}
          chooseOptions={{
            icon: isUploadingPhoto ? "pi pi-spin pi-spinner" : "pi pi-upload",
            className: "p-button-outlined p-button-secondary",
          }}
          pt={{
            input: { "aria-label": "Upload profile photo" },
          }}
        />
      </div>
      <div className={S.fieldsGrid}>
        {dynamicFieldGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            <div className="grid grid-cols-2 gap-10">
              {group.fields.map(field => (
                <ProfileField
                  key={field.key}
                  field={field}
                  formData={formData}
                  isEditing={isEditing}
                  handleChange={handleChange}
                />
              ))}
            </div>

            {/* Divider */}
            {groupIndex !== FIELD_GROUPS.length && <hr className="my-12 border-gray-400" />}
          </div>
        ))}
      </div>
    </>
  );
}

ProfileDetailsTab.propTypes = {
  user: T.object,
};
